// The one player (docs/design/SIVUPALSTA.md "Shell"): a single <audio> element for the active line,
// shown as the player bar (phone: 56px above the tabs, only while a line is active; PC: at the foot
// of lane B). Lines do not render their own Kuuntele / Silmukka / Hidas row any more; they register
// with the margin (useLine / LinePlay) and call play() from the tap itself, so iOS lets it sound.
//
// The device volume and our recordings' levelling apply as everywhere (recordings/volume.ts patches
// HTMLMediaElement.prototype.play). Nothing animates.
import { useSyncExternalStore } from 'react';
import type { LineInfo } from './context';

export interface PlayerState {
  lineId: string | null;
  /** Index of the playing source in the line's sources. */
  source: number;
  playing: boolean;
  loop: boolean;
  slow: boolean;
  failed: boolean;
}

const SLOW_RATE = 0.75;

let state: PlayerState = { lineId: null, source: 0, playing: false, loop: false, slow: false, failed: false };
const listeners = new Set<() => void>();
let el: HTMLAudioElement | null = null;
/** The line playing now, when its sources are parts of one sequence (LineInfo.sequence). */
let sequence: Pick<LineInfo, 'id' | 'sources'> | null = null;

/** A sequence goes on to its next part on the same element (iOS lets a started element play on). */
function nextPart(): boolean {
  const a = el;
  if (!a || !sequence || sequence.id !== state.lineId || state.loop) return false;
  const next = state.source + 1;
  const src = sequence.sources[next];
  if (!src) return false;
  a.src = src.url;
  set({ source: next, failed: false });
  start(a);
  return true;
}

function set(p: Partial<PlayerState>): void {
  state = { ...state, ...p };
  for (const l of listeners) l();
}

function audio(): HTMLAudioElement | null {
  if (el || typeof Audio === 'undefined') return el;
  el = new Audio();
  el.preload = 'none';
  (el as HTMLAudioElement & { preservesPitch?: boolean }).preservesPitch = true;
  el.addEventListener('play', () => set({ playing: true, failed: false }));
  el.addEventListener('pause', () => set({ playing: false }));
  el.addEventListener('ended', () => {
    if (!nextPart()) set({ playing: false });
  });
  // a part that cannot be played (gone, offline) is skipped in a sequence, never stops the rest
  el.addEventListener('error', () => {
    if (!nextPart()) set({ playing: false, failed: true });
  });
  return el;
}

function start(a: HTMLAudioElement): void {
  a.loop = state.loop;
  a.playbackRate = state.slow ? SLOW_RATE : 1;
  // (an engine without media support may return nothing at all)
  const p = a.play() as Promise<void> | undefined;
  p?.catch(() => set({ playing: false, failed: true }));
}

/** Play a line from its first (or the given) source. Call it from the tap itself (iOS). */
export function playLine(info: Pick<LineInfo, 'id' | 'sources' | 'sequence'>, source = 0): void {
  const a = audio();
  const src = info.sources[source];
  if (!a || !src) return;
  sequence = info.sequence ? info : null;
  if (state.lineId === info.id && state.source === source && !a.paused) {
    a.pause();
    return;
  }
  const url = new URL(src.url, location.href).href;
  if (a.src !== url) a.src = src.url;
  else a.currentTime = 0;
  set({ lineId: info.id, source, failed: false });
  start(a);
}

/**
 * Play a line from the start, every time: a tap while it plays starts it over instead of pausing it
 * (a Loppukoe question's clip, exercises/ItemView.tsx: a short word must never read as "won't replay").
 */
export function replayLine(info: Pick<LineInfo, 'id' | 'sources' | 'sequence'>, source = 0): void {
  const a = audio();
  if (!a || !info.sources[source]) return;
  if (state.lineId === info.id && state.source === source && !a.paused) {
    a.currentTime = 0;
    return;
  }
  playLine(info, source);
}

/** Play / pause the current line. */
export function togglePlayer(info: Pick<LineInfo, 'id' | 'sources' | 'sequence'>): void {
  const a = audio();
  if (!a) return;
  if (state.lineId !== info.id || !a.src) {
    playLine(info, 0);
    return;
  }
  if (a.paused) start(a);
  else a.pause();
}

export function setLoop(on: boolean): void {
  if (el) el.loop = on;
  set({ loop: on });
}

export function setSlow(on: boolean): void {
  if (el) el.playbackRate = on ? SLOW_RATE : 1;
  set({ slow: on });
}

/** Stop and forget the line (the player bar closed). */
export function stopPlayer(): void {
  el?.pause();
  sequence = null;
  set({ lineId: null, source: 0, playing: false, failed: false });
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function usePlayer(): PlayerState {
  return useSyncExternalStore(
    subscribe,
    () => state,
    () => state,
  );
}
